import Head from "next/head";
import { FunctionComponent } from "react";
import { useFragment, graphql } from "react-relay";

import { GQLEnumPostStatusEnum } from "@framework/__generated__/graphql/enums";
import {
  ParselyTagsContainer_feedContentResource$key,
  ResourceTypeEnum,
} from "@framework/__generated__/relay/ParselyTagsContainer_feedContentResource.graphql";
import { getCommunityCode } from "@framework/analytics/analytics.utils";
import useParselyTraining from "@framework/analytics/hooks/use-parsely-training";
import { useConfigContext } from "@voxmedia/duet-context/context/config-context";
import { useGlobalDataContext } from "@voxmedia/duet-context/context/global-data-context";
import { withGracefulErrorBoundary } from "@voxmedia/duet-ui/errors/GracefulErrorBoundary";
import { getPageType } from "@voxmedia/duet-utils/analytics";

interface Props {
  feedContentResource: ParselyTagsContainer_feedContentResource$key;
  pageTypeOverride?: string;
}

const getParselyType = (resourceType: ResourceTypeEnum): "post" | "index" => {
  if (
    resourceType === "videoGame" ||
    resourceType === "quickPost" ||
    resourceType === "venue"
  ) {
    return "index";
  }

  return "post";
};

const ParselyTags: FunctionComponent<Props> = ({
  pageTypeOverride,
  ...props
}) => {
  const node = useFragment(
    graphql`
      fragment ParselyTagsContainer_feedContentResource on ContentResourceInterface {
        status
        title
        permalink
        resourceType
        originalPublishedAt
        chorusId
        id

        ... on FeedContentResourceInterface {
          # @excludeCacheKey: assignment/unassignment of primary category will result in invalidation
          # of the resource cache key, so including the key is not needed
          primaryCategory @excludeCacheKey {
            title
            slug
          }
          # @excludeCacheKey: assignment/unassignment of categories will result in invalidation
          # of the resource cache key, so including the key is not needed
          internalSuperCats @excludeCacheKey {
            slug
          }
          # @excludeCacheKey: assignment/unassignment of categories will result in invalidation
          # of the resource cache key, so including the key is not needed
          superCats @excludeCacheKey {
            slug
          }
          # @excludeCacheKey: assignment/unassignment of categories will result in invalidation
          # of the resource cache key, so including the key is not needed
          internalCategories @excludeCacheKey {
            slug
          }
          # @excludeCacheKey: assignment/unassignment of categories will result in invalidation
          # of the resource cache key, so including the key is not needed
          categories @excludeCacheKey {
            slug
          }
          # @excludeCacheKey: assignment/unassignment of the package shadow taxonomy will result in
          # invalidation of the resource cache key, so including the key is not needed
          package @excludeCacheKey {
            chorusId
            id
          }
          streams(first: 100) {
            # @excludeCacheKey: assignment/unassignment of the package shadow taxonomy will result in
            # invalidation of the resource cache key, so including the key is not needed
            nodes @excludeCacheKey {
              chorusId
              id
            }
          }
          # @excludeCacheKey: changes to a resource's authors will result in invalidation of the
          # resource cache key
          authors @excludeCacheKey {
            id
            name
          }
          games {
            # @excludeCacheKey: assignment/unassignment of games will result in invalidation
            # of the resource cache key, so including the key is not needed
            nodes @excludeCacheKey {
              title
            }
          }
        }

        ledeMedia {
          ... on LedeMediaImageType {
            __typename
            image {
              thumbnails {
                social {
                  url
                }
              }
            }
          }
          ... on LedeMediaVideoType {
            __typename
          }
        }
        social {
          image {
            thumbnails {
              social {
                url
              }
            }
          }
        }
        featuredImage {
          image {
            thumbnails {
              social {
                url
              }
            }
          }
        }

        ... on PostResourceType {
          template
        }
      }
    `,
    props.feedContentResource,
  );

  const { siteSlug, placeholderImageUrl } = useGlobalDataContext().siteSettings;
  const { tagsToInverse, enableTraining } = useConfigContext().parselyConfig;
  const { communityCanonicalDomain, communityName, isPrimaryCommunity } =
    useConfigContext().communityConfig;

  const {
    internalSuperCats,
    internalCategories,
    superCats,
    primaryCategory,
    resourceType,
    categories,
    streams,
    title,
    permalink,
    social,
    originalPublishedAt,
    authors,
    status,
    ledeMedia,
    featuredImage,
    package: pkg,
    template,
  } = node;

  const pageTypeTag = pageTypeOverride || getPageType(resourceType, !!template);

  const communityCode = getCommunityCode(
    communityName,
    communityCanonicalDomain,
    isPrimaryCommunity,
  );

  // Tags include community slug
  const tags = [
    siteSlug,
    communityCode,
    `pagetype:${pageTypeTag}`,
    [
      ...(internalCategories || []),
      ...(categories || []),
      ...(internalSuperCats || []),
      ...(superCats || []),
    ].map((c) => c.slug),
  ];

  if (node.resourceType === "package") {
    tags.push(`package-${node.chorusId || node.id}`);
  }

  // Add Package ID
  if (pkg) {
    tags.push(`package-${pkg.chorusId ? pkg.chorusId : pkg.id}`);
  }

  // Add Streams to tags
  if (streams?.nodes.length) {
    streams.nodes.map((stream) => {
      tags.push(`stream-${stream.chorusId ? stream.chorusId : stream.id}`);
    });
  }

  // Add games to tags
  node.games?.nodes.map((game) => {
    tags.push(`game:${game.title}`);
  });

  // Add content types to tags
  // Note: This logic is cribbed from how we set `mediaVariation` in
  // `use-content-card-date-mapper.utils.ts` and may evolve
  if (
    resourceType !== "package" &&
    resourceType !== "stream" &&
    ledeMedia?.__typename === "LedeMediaVideoType"
  ) {
    tags.push("contenttype:video");
  }
  if (node.categories?.some((category) => category.slug === "podcasts")) {
    tags.push("contenttype:podcast");
  }

  const tagsFlat: string[] = tags.flat();
  // For each tag not present in the tagsToInverse list, add a corresponding
  // virtual "not-" tag for Parse.ly. Polygon uses this to generate
  // "not-guides" tags, which power its Most Popular sidebar.
  tagsToInverse.forEach((inv: string) => {
    if (!tagsFlat.some((tag) => tag.toLowerCase() === inv)) {
      tagsFlat.push(`not-${inv}`);
    }
  });

  const tagsUnique = [...new Set(tagsFlat)].filter(
    (t) => typeof t === "string" && t.length > 0,
  );

  // Special cases for SBNation
  if (communityCode && communityCode.includes("sbnation")) {
    // Replace "sbnation" (on its own) with "sbn"
    const index = tagsUnique.indexOf("sbnation");
    if (index !== -1) {
      tagsUnique[index] = "sbn";
    }

    // If a tag contains ".sbnation", delete it from the list
    const subdomainIndex = tagsUnique.findIndex((tag) =>
      tag.includes(".sbnation"),
    );
    if (subdomainIndex !== -1) {
      tagsUnique.splice(subdomainIndex, 1);
    }
  }

  useParselyTraining({ enabled: enableTraining });

  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- GraphQL string status vs enum
  if (status !== GQLEnumPostStatusEnum.publish) {
    return null;
  }

  return (
    <Head>
      <meta name="parsely-type" content={getParselyType(resourceType)} />
      <meta name="parsely-title" content={title} />
      <meta name="parsely-link" content={permalink} />
      <meta
        name="parsely-image-url"
        content={
          social.image?.thumbnails.social.url ||
          (ledeMedia?.__typename === "LedeMediaImageType" &&
            ledeMedia.image.thumbnails.social.url) ||
          featuredImage?.image.thumbnails.social.url ||
          placeholderImageUrl.social
        }
      />
      <meta name="parsely-pub-date" content={originalPublishedAt || ""} />
      {primaryCategory && (
        <meta name="parsely-section" content={primaryCategory.slug} />
      )}
      <meta name="parsely-tags" content={tagsUnique.join(",")} />
      {authors?.map((author) => {
        return (
          <meta key={author.id} name="parsely-author" content={author.name} />
        );
      })}
    </Head>
  );
};

export default withGracefulErrorBoundary(ParselyTags, {
  context: "ParselyTagsErrorBoundary",
});
